/**
 * CAPTCHA token provider for the lead form (09-landing-page.md §3.2). The API's `POST
 * /public/leads` DTO always requires a non-empty `captchaToken` (T25 anti-abuse pipeline, Decision
 * B); the *verifier* it's checked against is swappable server-side via `CAPTCHA_PROVIDER`
 * (`dev` locally — any non-empty token passes; a real fail-closed reCAPTCHA/hCaptcha verifier in
 * prod). This module mirrors that on the client: `VITE_CAPTCHA_PROVIDER` defaults to `'dev'`, which
 * needs no external script/site key and just mints a local placeholder token. Setting
 * `VITE_CAPTCHA_PROVIDER=recaptcha` + `VITE_RECAPTCHA_SITE_KEY` later swaps in the real invisible
 * v3 flow without any change to `LeadForm`'s submit path — it only ever calls `getCaptchaToken()`.
 *
 * No real site key is ever hardcoded here; if `VITE_RECAPTCHA_SITE_KEY` isn't set while
 * `VITE_CAPTCHA_PROVIDER=recaptcha`, this silently falls back to the dev token rather than
 * throwing, so a misconfigured env never blocks the whole form.
 */

type CaptchaProvider = "dev" | "recaptcha";

const PROVIDER: CaptchaProvider =
  import.meta.env.VITE_CAPTCHA_PROVIDER === "recaptcha" ? "recaptcha" : "dev";

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

export const CAPTCHA_PROVIDER = PROVIDER;
export const CAPTCHA_SITE_KEY_CONFIGURED = Boolean(RECAPTCHA_SITE_KEY);

interface GrecaptchaLike {
  ready(cb: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: GrecaptchaLike;
  }
}

let scriptLoadPromise: Promise<void> | null = null;

/** Loads the reCAPTCHA v3 script async/deferred (perf notes §6 "loaded async/deferred, not
 * render-blocking") — only ever called when the real provider is configured. */
function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  scriptLoadPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load reCAPTCHA"));
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

/**
 * Resolves a CAPTCHA token for one form submission. Dev mode: instant, no network. Real mode:
 * loads the script on first use, waits for `grecaptcha.ready`, then executes an invisible
 * (v3-style) check for the given `action` — never a blocking visual puzzle for the common case,
 * per §3.2.
 */
export async function getCaptchaToken(
  action = "public_lead_submit",
): Promise<string> {
  if (PROVIDER === "recaptcha" && RECAPTCHA_SITE_KEY) {
    try {
      await loadRecaptchaScript(RECAPTCHA_SITE_KEY);
      const grecaptcha = window.grecaptcha;
      if (grecaptcha) {
        await new Promise<void>((resolve) => grecaptcha.ready(resolve));
        return await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
      }
    } catch {
      // Falls through to the dev token below — a CAPTCHA *script* load failure must never be the
      // reason a legitimate visitor can't submit the form; the server-side verifier is the real
      // gate (fail-closed there per T25), this is just best-effort on the client.
    }
  }
  // Dev/no-provider placeholder — the API's `dev` CaptchaVerifier accepts any non-empty token.
  return `dev-${action}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
